import {Helmet} from '@dr.pogodin/react-helmet';
import {ColorSchemeType, DataStatus, Segment} from 'platform/components';
import {Box, HStack, Text, VStack, Clickable, Hide} from 'platform/foundation';
import {match} from 'ts-pattern';

import {always} from 'ramda';

import type {KanbanSortBy} from '@dms/api/core';
import {BusinessCaseType} from '@dms/api/finance';
import i18n from '@dms/i18n';
import {businessCaseRoutes, interestRoutes} from '@dms/routes';
import {Main, queryParams, usePermissions} from '@dms/shared';

import {composePath, DOT_CHARACTER, pushGtmEvent, useNavigate} from 'shared/web';

import {KanbanCard} from './components/KanbanCard';
import {KanbanCardSkeleton} from './components/KanbanCardSkeleton';
import {KanbanUserAvatar} from './components/KanbanUserAvatar';
import {Settings} from './components/Settings';
import {KANBAN_COLUMNS} from './constants/kanbanColumns';
import {KANBAN_SEGMENTS} from './constants/kanbanSegments';
import {useKanbanControl} from './hooks/useKanbanControl';
import {useKanbanSettings} from './hooks/useKanbanSettings';
import {getCardOwner} from './utils/getCardOwner';
import {getCardOwnerColor} from './utils/getCardOwnerColor';
import {getCardsForColumnValue} from './utils/getCardsForColumnValue';
import {getNonHiddenSelectedUserIds} from './utils/getNonHiddenSelectedUserIds';
import {getWatchedUserColor} from './utils/getWatchedUserColor';
import {isColumnVisible} from './utils/isColumnVisible';

export function OpportunitiesKanban() {
  const navigate = useNavigate();

  const [canCreateInterest] = usePermissions({
    permissionKeys: ['interestCreate'],
    scopes: {
      interestCreate: {
        // general creation, not scoped to a participation or a branch
        participation: 'SKIP',
        // general creation, not scoped to a participation or a branch
        branchId: 'SKIP',
      },
    },
  });

  const {
    businessCaseType,
    businessCases,
    interests,
    isLoading,
    isApplyingFilter,
    isError,
    onFilterChange,
  } = useKanbanControl();

  const handleFilterChange = (
    nonHiddenSelectedUserIds: string[],
    sortBy: KanbanSortBy,
    branchIds: string[]
  ) => {
    onFilterChange(businessCaseType, nonHiddenSelectedUserIds, sortBy, branchIds);
  };

  const {
    currentUser,
    users,
    settings,
    isLoading: isSettingsLoading,
    isError: isSettingsErrored,
    onKanbanSettingsChange,
  } = useKanbanSettings(handleFilterChange);

  const hasVisibleUsers = getNonHiddenSelectedUserIds(settings.selectedUsers).length > 0;
  const isBoardLoading = hasVisibleUsers && isLoading;
  const visibleBusinessCases = hasVisibleUsers ? businessCases : undefined;
  const visibleInterests = hasVisibleUsers ? interests : undefined;

  return (
    <>
      <Helmet title={i18n.t('page.opportunitiesKanban.title')} />
      <Main isFullHeight padding={0}>
        <Box
          position="sticky"
          backgroundColor="palettes.neutral.10.100"
          top={0}
          left={0}
          right={0}
          zIndex="CONTENT"
          paddingHorizontal={[2, 4]}
        >
          <DataStatus
            data={settings}
            isLoading={isSettingsLoading}
            isError={isSettingsErrored}
            spinnerSize="small"
          >
            {({data}) => (
              <HStack width="100%" minHeight={[12, 16]} align="center" justify="space-between">
                <Segment<BusinessCaseType>
                  value={businessCaseType}
                  options={KANBAN_SEGMENTS}
                  onChange={(value) => {
                    onFilterChange(
                      value,
                      getNonHiddenSelectedUserIds(data.selectedUsers),
                      data.sortBy,
                      data.branchIds
                    );
                  }}
                />
                <HStack spacing={2} align="center">
                  <Hide when={data.selectedUsers.length < 2} onMobile>
                    {data.selectedUsers.map((user, userIndex) => (
                      <Clickable
                        key={`kanban-settings-${user.id}`}
                        isDisabled={
                          !user.isHidden &&
                          data.selectedUsers.filter((user) => !user.isHidden).length === 1
                        }
                        onClick={() => {
                          onKanbanSettingsChange({
                            visibleColumns: data.visibleColumns,
                            sortBy: data.sortBy,
                            branchIds: data.branchIds,
                            selectedUsers: data.selectedUsers.map((item) =>
                              item.id === user.id ? {...item, isHidden: !item.isHidden} : item
                            ),
                          });
                        }}
                      >
                        <Box opacity={user.isHidden ? 0.2 : 1}>
                          <KanbanUserAvatar
                            name={user.fullName}
                            color={getWatchedUserColor(userIndex)}
                          />
                        </Box>
                      </Clickable>
                    ))}
                  </Hide>
                  <Settings
                    currentUserId={currentUser?.id ?? ''}
                    users={users}
                    settings={data}
                    onSave={onKanbanSettingsChange}
                  />
                </HStack>
              </HStack>
            )}
          </DataStatus>
        </Box>
        <DataStatus
          isLoading={isBoardLoading || isSettingsLoading}
          isError={isError}
          isEmpty={
            !isBoardLoading &&
            !isApplyingFilter &&
            !isSettingsLoading &&
            !visibleBusinessCases?.rows?.length &&
            !visibleInterests?.rows?.length
          }
          data={settings}
          // Not "no business cases": the board lists interests too, and the query always filters
          // by owner, type, state and branch, so this is a filtered-to-empty view rather than an
          // empty dataset.
          emptyMessage={i18n.t('general.labels.noResultsFound')}
          // Interest, not business case: businessCaseRoutes has no create route, a business
          // case only comes into being by converting an interest.
          action={
            canCreateInterest
              ? {
                  title: i18n.t('entity.interest.actions.newInterest'),
                  variant: 'primary',
                  onClick: () => navigate(interestRoutes.create),
                }
              : undefined
          }
          minHeight={200}
        >
          {({data}) => {
            const visibleColumns = KANBAN_COLUMNS.filter((kanbanColumn) =>
              isColumnVisible(data, businessCaseType, kanbanColumn.key)
            ).map((column, columnIndex) => ({
              column,
              columnIndex,
              cards: getCardsForColumnValue(
                column.key,
                data.sortBy,
                visibleBusinessCases,
                visibleInterests
              ),
            }));

            return (
              <Box width="100%" height="100%" paddingHorizontal={[2, 4]} paddingBottom={[2, 4]}>
                <HStack spacing={2} height="100%" width="100%">
                  {visibleColumns.map(({column, columnIndex, cards}) => (
                    <Box
                      key={`business-case-kanban-${column.key}`}
                      flex={1}
                      minWidth={0}
                      height="100%"
                    >
                      <Box
                        borderRadius="small"
                        width="100%"
                        height="100%"
                        backgroundColor="palettes.neutral.30.100"
                        padding={2}
                      >
                        <VStack width="100%" height="100%" spacing={3}>
                          <Box
                            position="sticky"
                            top={[12, 16]}
                            zIndex="CONTENT"
                            backgroundColor="palettes.neutral.30.100"
                          >
                            <HStack spacing={1} align="center">
                              <Text size="small" alternative color="primary">
                                {i18n.t(column.label)}
                              </Text>
                              <Text size="small" color="tertiary">
                                {DOT_CHARACTER}
                              </Text>
                              <Text size="small" color="tertiary">
                                {cards.length || 0}
                              </Text>
                            </HStack>
                          </Box>
                          {isApplyingFilter ? (
                            <KanbanCardSkeleton data-testid={`kanban-${column.key}`} />
                          ) : (
                            cards.map((card) => {
                              const detailHref = composePath(
                                match([card.origin, businessCaseType])
                                  .with(
                                    ['business-case', 'BUYING'],
                                    () => businessCaseRoutes.buying
                                  )
                                  .with(
                                    ['business-case', 'SELLING'],
                                    () => businessCaseRoutes.selling
                                  )
                                  .otherwise(() => interestRoutes.detailOverview),
                                {
                                  params: {id: card.id},
                                  queryParams: {
                                    source: 'opportunities-kanban',
                                  },
                                }
                              );
                              // The unread indicator opens the record's thread at its oldest unread message.
                              const communicationHref = composePath(
                                card.origin === 'business-case'
                                  ? businessCaseRoutes.communication
                                  : interestRoutes.detailCommunication,
                                {
                                  params: {id: card.id},
                                  queryParams: {
                                    source: 'opportunities-kanban',
                                    [queryParams.COMMUNICATION_SCROLL_TO_OLDEST_UNREAD]: 'true',
                                  },
                                }
                              );

                              return (
                                <KanbanCard
                                  key={`${businessCaseType}-${column.key}-${card.id}-${card.origin}`}
                                  card={card}
                                  ownerColor={getCardOwnerColor(data.selectedUsers, card.ownerId)}
                                  owner={getCardOwner(data.selectedUsers, card.ownerId)}
                                  colorStripe={match<number, ColorSchemeType>(columnIndex)
                                    .with(0, always('neutral'))
                                    .with(1, always('yellow'))
                                    .with(2, always('orange'))
                                    .with(3, always('teal'))
                                    .with(4, always('blue'))
                                    .with(5, always('purple'))
                                    .with(6, always('magenta'))
                                    .with(7, always('green'))
                                    .otherwise(always('black'))}
                                  detailHref={detailHref}
                                  communicationHref={communicationHref}
                                  onDetailClick={() => {
                                    pushGtmEvent({
                                      event: 'button_click',
                                      event_id: 'opportunities_kanban_card_click',
                                      value: {
                                        card_id: card.id,
                                        card_origin: card.origin,
                                        card_business_case_type: businessCaseType,
                                      },
                                    });
                                  }}
                                  customerId={card.customerId}
                                  sortBy={data.sortBy}
                                  data-testid={`kanban-card-${card.origin}-${card.id}`}
                                />
                              );
                            })
                          )}
                        </VStack>
                      </Box>
                    </Box>
                  ))}
                </HStack>
              </Box>
            );
          }}
        </DataStatus>
      </Main>
    </>
  );
}
